import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";

import { Tag } from "primereact/tag";

import { ConfirmDialog, Dialog, DialogFooter, DialogHeader, FormField } from "@/ds";
import { TipoObraTag } from "@/shared/components";
import {
  CAMPO_INICIO,
  CAMPO_NOME,
  CAMPO_ORIGEM,
  CANCELAR_LABEL,
  DICA_INICIO,
  DICA_NOME,
  DICA_ORIGEM,
  SALVAR_LABEL,
} from "./projecao-dialog.constants";
import { CotacaoDialog } from "../cotacao-dialog";
import { JustificativaField } from "@/shared/components";
import { ProjecaoFormularioView } from "../projecao-formulario/view-projecao-formulario";
import modal from "../planejamento-controle.modal.module.scss";
import styles from "./projecao-dialog.module.scss";
import type { ProjecaoDialogViewModel } from "./types-projecao-dialog";

const RODAPE_DIALOGO = { footer: { className: modal.dialogoRodape } };
function CampoLeitura({
  label,
  valor,
  dica,
  testId,
}: {
  label: string;
  valor: string;
  dica: string;
  testId: string;
}) {
  return (
    <FormField.Root disabled help={dica}>
      <FormField.Label>{label}</FormField.Label>
      <FormField.Control>
        <InputText value={valor} readOnly data-testid={testId} />
      </FormField.Control>
      <FormField.Help />
    </FormField.Root>
  );
}

export function ProjecaoDialogView({
  formId,
  titulo,
  estado,
  errorMessage,
  recarregar,
  escopo,
  tipoObraCodigo,
  classificacaoRotulo,
  obra,
  cotacaoDaObra,
  servicos,
  materiais,
  custoPadrao,
  semCustoPadrao,
  fator,
  classificacao,
  totalEscopo,
  justificativa,
  tabela,
  isSubmitting,
  submitError,
  moedaPendente,
  definirCotacaoPendente,
  cotacaoDialog,
  descarte,
  salvarDesabilitado,
  salvarAviso,
  enviar,
  cancelar,
}: ProjecaoDialogViewModel) {
  const bloqueado = isSubmitting || estado !== "success";

  return (
    <>
      <Dialog
        visible
        size="xl"
        className={modal.dialogoFicha}
        contentClassName={modal.corpo}
        onHide={cancelar}
        locked={isSubmitting}
        focusOnShow={false}
        header={<DialogHeader title={titulo} />}
        pt={RODAPE_DIALOGO}
        footer={
          <DialogFooter
            className={modal.rodape}
            cancelLabel={CANCELAR_LABEL}
            confirmLabel={SALVAR_LABEL}
            confirmButtonProps={{
              disabled: salvarDesabilitado,
            }}
            formId={formId}
            loading={isSubmitting}
            locked={isSubmitting}
            onCancel={cancelar}
            testId="projecao"
          />
        }
        data-testid="projecao-dialog"
      >
        <form id={formId} className={styles.form} onSubmit={enviar} noValidate>
          <header className={styles.identificacao}>
            <TipoObraTag tipo={tipoObraCodigo} />
            <div className={styles.textos}>
              <h2 className={styles.nome} data-testid="projecao-nome">
                {escopo.nome}
              </h2>
              <p className={styles.contexto} data-testid="projecao-contexto">
                {escopo.tipoObra} · {obra.embarcacao} · {escopo.centroCusto}
              </p>
            </div>
            {classificacaoRotulo ? (
              <Tag
                className={styles.classificacaoTag}
                value={classificacaoRotulo}
                severity="info"
                data-testid="projecao-classificacao-tag"
              />
            ) : null}
          </header>
          <div className={styles.dados}>
            <CampoLeitura
              label={CAMPO_NOME}
              valor={escopo.nome}
              dica={DICA_NOME}
              testId="projecao-campo-nome"
            />
            <CampoLeitura
              label={CAMPO_ORIGEM}
              valor={escopo.origem}
              dica={DICA_ORIGEM}
              testId="projecao-campo-origem"
            />
            <CampoLeitura
              label={CAMPO_INICIO}
              valor={obra.inicioMensalizacao}
              dica={DICA_INICIO(obra.dataInicio)}
              testId="projecao-campo-inicio"
            />
          </div>

          <ProjecaoFormularioView
            estado={estado}
            errorMessage={errorMessage}
            recarregar={recarregar}
            enviando={isSubmitting}
            cotacaoDaObra={cotacaoDaObra}
            servicos={servicos}
            materiais={materiais}
            custoPadrao={custoPadrao}
            semCustoPadrao={semCustoPadrao}
            fator={fator}
            classificacao={classificacao}
            totalEscopo={totalEscopo}
            tabela={tabela}
            moedaPendente={moedaPendente}
            definirCotacaoPendente={definirCotacaoPendente}
          />

          {justificativa ? (
            <>
              <Message
                className="p-message-block"
                severity="info"
                text={justificativa.aviso}
                data-testid="projecao-aviso-aprovacao"
              />
              <JustificativaField
                labelClassName={modal.rotuloCampo}
                rows={3}
                value={justificativa.valor}
                onChange={justificativa.digitar}
                error={justificativa.erro}
                disabled={bloqueado}
                testIds={{
                  campo: "projecao-justificativa",
                  contador: "projecao-justificativa-contador",
                  erro: "projecao-justificativa-error",
                }}
              />
            </>
          ) : null}

          {salvarAviso ? (
            <Message
              className="p-message-block"
              severity="error"
              text={salvarAviso}
              data-testid="projecao-salvar-aviso"
            />
          ) : null}

          {submitError ? (
            <Message
              className="p-message-block"
              severity="error"
              text={submitError}
              data-testid="projecao-submit-error"
            />
          ) : null}
        </form>
      </Dialog>
      {cotacaoDialog ? <CotacaoDialog {...cotacaoDialog} /> : null}
      {descarte ? <ConfirmDialog {...descarte} /> : null}
    </>
  );
}
